/**
 * Receipt canvas toolkit — the single layout engine shared by every renderer.
 *
 * Lifted from the original `sale-receipt`/`order-receipt` canvas builders (which
 * each carried an identical copy of these op factories). A renderer creates a
 * canvas with `createReceiptCanvas`, queues draw ops (`lines`, `row`, `rule`,
 * …), then calls `finish()` which measures everything offscreen, renders to a
 * white canvas at the printable width, and returns a PNG `data:` URL ready for
 * `starPrintService.printImage`.
 */
import QRCode from 'qrcode';

import type { PaperWidth } from './types';

const FONT_STACK = "'Helvetica Neue', Arial, sans-serif";

export type Align = 'left' | 'center' | 'right';

export type InlineTextRun = {
  text: string;
  weight?: number;
};

/** A draw op: renders itself at `y` on the given context and returns its height. */
type Op = (ctx: CanvasRenderingContext2D, y: number) => number;

export interface ReceiptCanvasOptions {
  paperWidth?: PaperWidth;
  /** Global font-size multiplier (the order ticket's 'large' variant uses 1.6). */
  sizeMul?: number;
  /**
   * Floor the finished canvas to at least this many 576-baseline dots, padding the
   * bottom with trailing whitespace. Lets a short ticket still print enough paper to
   * grab and tear off. Omit for no floor (default).
   */
  minHeight?: number;
}

export interface ReceiptCanvas {
  /** Canvas width in dots. */
  readonly width: number;
  /** Scale a 576-baseline pixel value to the current paper width. */
  px: (n: number) => number;
  /** Draw wrapped/aligned text (string auto-wraps; string[] prints verbatim). */
  lines: (
    text: string | string[],
    size: number,
    weight: number,
    align: Align,
    indent?: number
  ) => void;
  /** Draw one line containing adjacent text runs with independent font weights. */
  inlineText: (runs: InlineTextRun[], size: number, align?: Align) => void;
  /** Two columns: `left[]` flush-left and `right[]` flush-right, shared baseline. */
  cols: (left: string[], right: string[], size: number, weight: number) => void;
  /** A single label/value row (label left, value right). */
  row: (label: string, value: string, size: number, weight: number) => void;
  /** Wrapping item name on the left, value pinned to the right of the first line. */
  itemRow: (name: string, value: string, size: number, weight: number) => void;
  /** Horizontal rule with top/bottom margins. */
  rule: (mt?: number, mb?: number) => void;
  /** Vertical whitespace. */
  space: (h: number) => void;
  /** Center an already-rendered canvas (e.g. a QR code). */
  image: (img: HTMLCanvasElement | null, size: number) => void;
  /** Measure offscreen then render; returns a PNG `data:` URL. */
  finish: () => string;
}

/** Word-wraps text to `maxWidth`, hard-breaking any single word that overflows. */
export function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];

  const lines: string[] = [];
  let current = '';

  words.forEach((word) => {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width <= maxWidth) {
      current = test;
      return;
    }
    if (current) lines.push(current);
    if (ctx.measureText(word).width <= maxWidth) {
      current = word;
      return;
    }
    // Single word too wide → break it character-by-character.
    let chunk = '';
    word.split('').forEach((ch) => {
      if (ctx.measureText(chunk + ch).width <= maxWidth) {
        chunk += ch;
      } else {
        if (chunk) lines.push(chunk);
        chunk = ch;
      }
    });
    current = chunk;
  });

  if (current) lines.push(current);
  return lines.length ? lines : [''];
}

export function createReceiptCanvas(opts: ReceiptCanvasOptions = {}): ReceiptCanvas {
  const width = opts.paperWidth ?? 576;
  const scale = width / 576;
  const sizeMul = opts.sizeMul ?? 1;
  const px = (n: number) => Math.round(n * scale);
  const font = (size: number, weight = 400) => `${weight} ${px(size * sizeMul)}px ${FONT_STACK}`;
  const lh = (size: number) => Math.round(px(size * sizeMul) * 1.35);

  const pad = px(28);
  const padTop = px(28);
  const padBottom = px(40);
  const ruleW = Math.max(1, px(2));

  const ops: Op[] = [];

  const lines = (
    text: string | string[],
    size: number,
    weight: number,
    align: Align,
    indent = 0
  ) => {
    const lineH = lh(size);
    ops.push((ctx, y) => {
      ctx.font = font(size, weight);
      ctx.textBaseline = 'top';
      ctx.textAlign = align;
      const maxW = width - pad * 2 - indent;
      const arr = Array.isArray(text) ? text : wrapText(ctx, text, maxW);
      let x = pad + indent;
      if (align === 'center') x = width / 2;
      else if (align === 'right') x = width - pad;
      arr.forEach((ln, i) => ctx.fillText(ln, x, y + i * lineH));
      return arr.length * lineH;
    });
  };

  const cols = (left: string[], right: string[], size: number, weight: number) => {
    const lineH = lh(size);
    ops.push((ctx, y) => {
      ctx.font = font(size, weight);
      ctx.textBaseline = 'top';
      left.forEach((ln, i) => {
        ctx.textAlign = 'left';
        ctx.fillText(ln, pad, y + i * lineH);
      });
      right.forEach((ln, i) => {
        ctx.textAlign = 'right';
        ctx.fillText(ln, width - pad, y + i * lineH);
      });
      return Math.max(left.length, right.length) * lineH;
    });
  };

  const inlineText = (runs: InlineTextRun[], size: number, align: Align = 'left') => {
    const lineH = lh(size);
    ops.push((ctx, y) => {
      ctx.textBaseline = 'top';
      const widths = runs.map((run) => {
        ctx.font = font(size, run.weight ?? 400);
        return ctx.measureText(run.text).width;
      });
      const totalWidth = widths.reduce((sum, value) => sum + value, 0);
      let x = pad;
      if (align === 'center') x = (width - totalWidth) / 2;
      else if (align === 'right') x = width - pad - totalWidth;

      runs.forEach((run, index) => {
        ctx.font = font(size, run.weight ?? 400);
        ctx.textAlign = 'left';
        ctx.fillText(run.text, x, y);
        x += widths[index];
      });
      return lineH;
    });
  };

  const row = (label: string, value: string, size: number, weight: number) => {
    const lineH = lh(size);
    ops.push((ctx, y) => {
      ctx.font = font(size, weight);
      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';
      ctx.fillText(label, pad, y);
      ctx.textAlign = 'right';
      ctx.fillText(value, width - pad, y);
      return lineH;
    });
  };

  const itemRow = (name: string, value: string, size: number, weight: number) => {
    const lineH = lh(size);
    ops.push((ctx, y) => {
      ctx.font = font(size, weight);
      ctx.textBaseline = 'top';
      const valueW = ctx.measureText(value).width;
      const nameLines = wrapText(ctx, name, width - pad * 2 - valueW - px(16));
      ctx.textAlign = 'left';
      nameLines.forEach((ln, i) => ctx.fillText(ln, pad, y + i * lineH));
      ctx.textAlign = 'right';
      ctx.fillText(value, width - pad, y);
      return nameLines.length * lineH;
    });
  };

  const rule = (mt = px(14), mb = px(14)) => {
    ops.push((ctx, y) => {
      ctx.fillStyle = '#000000';
      ctx.fillRect(pad, y + mt, width - pad * 2, ruleW);
      return mt + ruleW + mb;
    });
  };

  const space = (h: number) => ops.push(() => h);

  const image = (img: HTMLCanvasElement | null, size: number) => {
    ops.push((ctx, y) => {
      if (!img) return 0;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, Math.round((width - size) / 2), y, size, size);
      return size;
    });
  };

  const finish = (): string => {
    // Measure offscreen with fonts/wrapping identical to the final draw.
    const scratch = document.createElement('canvas');
    scratch.width = width;
    scratch.height = 10;
    const sctx = scratch.getContext('2d')!;
    let measured = padTop;
    ops.forEach((op) => {
      measured += op(sctx, measured);
    });
    const floor = opts.minHeight ? px(opts.minHeight) : 0;
    const totalH = Math.max(Math.ceil(measured + padBottom), floor);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = totalH;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, totalH);

    let y = padTop;
    ops.forEach((op) => {
      ctx.fillStyle = '#000000';
      y += op(ctx, y);
    });

    return canvas.toDataURL('image/png');
  };

  return { width, px, lines, inlineText, cols, row, itemRow, rule, space, image, finish };
}

/** Render a QR code to an offscreen canvas. Returns null on failure (never throws). */
export async function renderQrCanvas(
  data: string,
  sizePx: number
): Promise<HTMLCanvasElement | null> {
  try {
    const canvas = document.createElement('canvas');
    await QRCode.toCanvas(canvas, data, {
      width: sizePx,
      margin: 1,
      color: { dark: '#000000', light: '#ffffff' },
    });
    return canvas;
  } catch (err) {
    console.error('[canvas-toolkit] QR generation failed, omitting it:', err);
    return null;
  }
}

/** The shared PointCraft branding footer (used by every receipt except the QR-rich sale receipt). */
export function appendBrandingFooter(c: ReceiptCanvas): void {
  c.space(c.px(18));
  c.rule();
  c.lines('Powered by PointCraft Inc', 23, 600, 'center');
  c.lines('Smart POS & Payments', 22, 400, 'center');
  c.lines('support@point-craft.com', 22, 400, 'center');
  c.lines('point-craft.com/support', 22, 400, 'center');
}
